import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radio, BookMarked, GraduationCap, Landmark, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import HeroImage from "@/components/HeroImage";
import Collapsible from "@/components/Collapsible";

const DEFAULTS = {
  eyebrow: "Direction académique",
  title: "Direction de la Recherche, des Bibliothèques et Musées",
  banner_image: "/media/library-shelves.jpg",
  intro:
    "La Direction de la Recherche, des Bibliothèques et Musées joue un rôle central dans la conduite de la recherche scientifique, la conservation et la diffusion des connaissances, l'accompagnement des chercheurs et la valorisation du patrimoine scientifique et culturel de l'UNISIC. Elle regroupe les laboratoires de recherche (CECOM), les structures documentaires, l'édition scientifique (PUSIC), la formation continue et les musées universitaires.",
};

const SUB_UNITS = [
  {
    key: "cecom",
    to: "/directions/bibliotheques-musees/cecom",
    Icon: Radio,
    color: "#0A2E52",
    eyebrow: "Centre d'études",
    title: "CECOM",
    subtitle: "Centre d'Études en Communication",
    desc: "Laboratoire de recherche et Cahiers Congolais de Communication.",
    banner: "/media/research-lab.jpg",
  },
  {
    key: "pusic",
    to: "/directions/bibliotheques-musees/pusic",
    Icon: BookMarked,
    color: "#D4AF37",
    eyebrow: "Presses universitaires",
    title: "PUSIC",
    subtitle: "Presses Universitaires des Sciences de l'Information et de la Communication",
    desc: "Publications universitaires et Unité de Reproduction des travaux académiques.",
    banner: "/media/modern-library.jpg",
  },
  {
    key: "formation",
    to: "/directions/bibliotheques-musees/formation",
    Icon: GraduationCap,
    color: "#144C86",
    eyebrow: "Développement des compétences",
    title: "Formation",
    subtitle: "Programmes de formation et perfectionnement",
    desc: "Formations continues, ateliers scientifiques et perfectionnement en documentation.",
    banner: "/media/university-hall.jpg",
  },
  {
    key: "biblio-musees",
    to: "/directions/bibliotheques-musees/bibliotheques-musees",
    Icon: Landmark,
    color: "#E63946",
    eyebrow: "Patrimoine et ressources",
    title: "Bibliothèques et Musées",
    subtitle: "Bibliothèques centrale et numérique, musées universitaires",
    desc: "Collections, ressources documentaires numériques et musées thématiques.",
    banner: "/media/museum-classical.jpg",
  },
];

export default function DirectionBibliothequesMusees() {
  const [content, setContent] = useState(DEFAULTS);
  useEffect(() => {
    api.get("/content/direction.bibliotheques_musees").then((r) => setContent({ ...DEFAULTS, ...(r.data || {}) })).catch(() => {});
  }, []);

  return (
    <div data-testid="direction-bm-hub">
      <section className="relative h-[46vh] min-h-[340px] overflow-hidden">
        <HeroImage src={content.banner_image} />
        <div className="absolute inset-0 hero-veil" />
        <div className="relative h-full mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-14">
          <div className="eyebrow" style={{ color: "#E5C158" }}>{content.eyebrow}</div>
          <h1 className="mt-3 text-white text-4xl sm:text-5xl font-bold tracking-tight max-w-4xl">
            {content.title}
          </h1>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-unisic-gold" />
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="eyebrow eyebrow-dark">Missions</div>
        <span className="divider-gold mt-3" />
        <p className="mt-6 text-lg text-slate-700 leading-relaxed max-w-4xl whitespace-pre-line">
          {content.intro}
        </p>

        <div className="mt-14">
          <div className="eyebrow eyebrow-dark">Quatre structures</div>
          <span className="divider-gold mt-3" />
          <div className="mt-8 grid gap-8 md:grid-cols-2">
            {SUB_UNITS.map((s) => (
              <Link
                key={s.key}
                to={s.to}
                className="group paper-card card-hover overflow-hidden flex flex-col"
                data-testid={`bm-card-${s.key}`}
              >
                <div className="relative h-52 overflow-hidden">
                  <img src={s.banner} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="inline-flex items-center justify-center w-10 h-10 rounded-sm mb-2" style={{ background: s.color, color: "#fff" }}>
                      <s.Icon className="w-5 h-5" />
                    </div>
                    <div className="text-[11px] tracking-[0.28em] uppercase text-white/85 font-semibold">
                      {s.eyebrow}
                    </div>
                    <h3 className="text-white text-xl font-bold mt-1 leading-tight">{s.title}</h3>
                    <p className="text-white/85 text-xs mt-1 leading-tight">{s.subtitle}</p>
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <p className="text-sm text-slate-600 leading-relaxed flex-1">{s.desc}</p>
                  <div className="mt-5 inline-flex items-center gap-2 text-unisic-blue font-semibold text-sm group-hover:text-unisic-gold transition-colors">
                    Accéder <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
